import { Calendar, Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { capitalizeFirst } from "@/utils/dateUtils";
import { useShiftDisplay } from "@/hooks/useShiftDisplay";
import type { GroupedAvailability } from "@/types/availability";

interface Props {
  group: GroupedAvailability;
  canEditOrDelete: boolean;
  isDeleting: boolean;
  onEdit: () => void;
  onDelete: () => void;
  showOperation?: boolean;
}

// Nombres de los turnos del grupo según su operación.
const GroupShifts = ({ group }: { group: GroupedAvailability }) => {
  const shiftDisplay = useShiftDisplay();
  return <>{group.shifts.map((s) => shiftDisplay.forOperations(s, [group.operationId])).join(", ")}</>;
};

export const AvailabilityGroupCard = ({
  group,
  canEditOrDelete,
  isDeleting,
  onEdit,
  onDelete,
  showOperation,
}: Props) => (
  <div className="bg-white rounded-lg shadow p-4 border-l-4 border-primary">
    <div className="space-y-3">
      {/* Header con fecha y botones */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Calendar className="h-4 w-4 text-primary" />
          {/* translate="no": evita que el traductor de Chrome
              reinterprete la fecha (ver formatLongDate) */}
          <span
            className="font-medium text-lg notranslate"
            translate="no"
          >
            {capitalizeFirst(group.formattedDate)}
          </span>
          {showOperation && (
            <span className="text-xs bg-primary-soft text-primary rounded px-2 py-0.5 ml-2" translate="no">
              {group.operationName}
            </span>
          )}
        </div>

        {/* Botones compactos */}
        <div className="flex space-x-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            disabled={!canEditOrDelete}
            className="text-primary hover:bg-primary-soft p-2 h-8 w-8 disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              !canEditOrDelete
                ? "No se puede editar la disponibilidad del día actual"
                : "Editar disponibilidad"
            }
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            disabled={isDeleting || !canEditOrDelete}
            className="text-red-600 hover:bg-red-50 p-2 h-8 w-8 disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              !canEditOrDelete
                ? "No se puede eliminar la disponibilidad del día actual"
                : "Eliminar disponibilidad"
            }
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Detalles de la disponibilidad */}
      <div className="space-y-2 text-sm text-gray-600">
        {/* Turnos */}
        <div>
          <span className="font-medium">Turnos:</span>{" "}
          <span className="text-primary font-medium">
            <GroupShifts group={group} />
          </span>
        </div>

        {/* Zonas */}
        <div>
          <span className="font-medium">Zonas:</span>{" "}
          <span className="text-green-600 font-medium">
            {group.svcs
              .map((svc) => svc.name)
              .join(", ")}
          </span>
        </div>

        {/* Vehículos */}
        {group.vehicles.length > 0 && (
          <div>
            <span className="font-medium">Vehículos:</span>{" "}
            <span className="text-purple-600 font-medium">
              {group.vehicles
                .map(
                  (vehicle) =>
                    `${vehicle.plate} (${vehicle.brand} ${vehicle.model})`
                )
                .join(", ")}
            </span>
          </div>
        )}

        {/* Estado de asignación */}
        {group.availabilities.some(
          (a) => a.assignedToday
        ) && (
            <div className="text-green-600 font-medium">
              ✓ Algunas disponibilidades asignadas hoy
            </div>
          )}

        {/* Contador de disponibilidades */}
        <div className="text-xs text-gray-500">
          {group.availabilities.length}{" "}
          disponibilidad(es) configurada(s)
        </div>
      </div>
    </div>
  </div>
);
